import { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';

const GuidedTourContext = createContext(null);

export function GuidedTourProvider({ children }) {
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const currentStep = isActive ? steps[currentStepIndex] || null : null;

  const markTourDone = useCallback(async () => {
    localStorage.setItem('onboarding_tour_completed', 'true');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: uc } = await supabaseData
          .from('user_companies')
          .select('company_id')
          .eq('user_id', user.id)
          .limit(1)
          .single();
        if (uc) {
          await supabaseData
            .from('companies')
            .update({ onboarding_tour_completed: true })
            .eq('id', uc.company_id);
        }
      }
    } catch (err) {
      // no-op
    }
  }, []);

  const startTour = useCallback((tourSteps) => {
    if (localStorage.getItem('onboarding_tour_completed') === 'true') return;
    setSteps(tourSteps);
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      completeTour();
    }
  }, [currentStepIndex, steps.length]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setSteps([]);
    setCurrentStepIndex(0);
    markTourDone();
  }, [markTourDone]);

  const completeTour = useCallback(() => {
    setIsActive(false);
    setSteps([]);
    setCurrentStepIndex(0);
    markTourDone();
  }, [markTourDone]);

  return (
    <GuidedTourContext.Provider value={{
      steps,
      currentStep,
      currentStepIndex,
      isActive,
      startTour,
      nextStep,
      skipTour,
      completeTour
    }}>
      {children}
    </GuidedTourContext.Provider>
  );
}

export function useGuidedTour() {
  const ctx = useContext(GuidedTourContext);
  if (!ctx) throw new Error('useGuidedTour must be used within GuidedTourProvider');
  return ctx;
}
